import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import {
    Flame,
    Send,
    Anchor,
    DollarSign,
    Award,
    AlertOctagon,
    Sparkles,
    Shield,
    ChevronDown,
    Radio,
} from "lucide-react";
import { toast } from "sonner";
import SovereignAnchor from "./SovereignAnchor";

const TYPE_META = {
    boarding: { icon: Anchor, accent: "hsl(45 85% 68%)", glow: "hsl(45 85% 65% / 0.30)" },
    fuel: { icon: Flame, accent: "hsl(25 90% 60%)", glow: "hsl(25 95% 60% / 0.30)" },
    revenue: { icon: DollarSign, accent: "hsl(153 60% 60%)", glow: "hsl(153 65% 55% / 0.30)" },
    level_up: { icon: Award, accent: "hsl(45 85% 75%)", glow: "hsl(45 85% 70% / 0.30)" },
    strike: { icon: AlertOctagon, accent: "hsl(350 75% 65%)", glow: "hsl(350 80% 60% / 0.28)" },
    breaking: { icon: Radio, accent: "hsl(45 95% 70%)", glow: "hsl(45 100% 65% / 0.40)" },
};

const formatRel = (iso, now) => {
    const t = new Date(iso).getTime();
    const n = new Date(now).getTime();
    const diff = Math.max(0, n - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
};

function StarCard({ item, now, onHype, onTransmit }) {
    const meta = TYPE_META[item.type] || TYPE_META.level_up;
    const Icon = meta.icon;
    const [bursting, setBursting] = useState(false);
    const isBreaking = item.type === "breaking" || item.pinned;

    const hype = async () => {
        setBursting(true);
        await onHype(item);
        setTimeout(() => setBursting(false), 600);
    };

    return (
        <article
            data-testid={`star-card-${item.id}`}
            data-pinned={isBreaking ? "1" : "0"}
            className="snap-center w-full max-w-md mx-auto h-[78vh] sm:h-[640px] rounded-3xl relative overflow-hidden flex flex-col"
            style={{
                background: isBreaking
                    ? "linear-gradient(180deg, hsl(0 0% 3%) 0%, hsl(40 50% 7%) 60%, hsl(151 55% 5%) 100%)"
                    : "linear-gradient(180deg, hsl(0 0% 4%) 0%, hsl(151 60% 5%) 100%)",
                boxShadow: isBreaking
                    ? `0 35px 90px -20px ${meta.glow}, inset 0 0 0 1.5px hsl(45 95% 65% / 0.45)`
                    : `0 30px 80px -20px ${meta.glow}, inset 0 0 0 1px hsl(40 50% 30% / 0.18)`,
            }}
        >
            {/* glow pad */}
            <div
                aria-hidden="true"
                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[120%] h-[60%] rounded-full blur-3xl opacity-50 pointer-events-none"
                style={{ background: meta.glow }}
            />

            {/* Sovereign Anchor watermark (high-fidelity vertical-video aesthetic) */}
            <div
                aria-hidden="true"
                className="absolute -bottom-8 -right-8 pointer-events-none select-none"
                style={{
                    color: meta.accent,
                    opacity: isBreaking ? 0.10 : 0.06,
                    filter: isBreaking ? "drop-shadow(0 0 24px hsl(45 95% 65% / 0.4))" : "none",
                    transform: "rotate(-8deg)",
                }}
            >
                <SovereignAnchor size={300} />
            </div>

            {/* Scanline overlay (broadcast feel — only on BREAKING) */}
            {isBreaking && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-screen"
                    style={{
                        backgroundImage:
                            "repeating-linear-gradient(0deg, hsl(45 95% 75%) 0px, hsl(45 95% 75%) 1px, transparent 1px, transparent 3px)",
                    }}
                />
            )}

            {/* Top rail — LIVE / BREAKING pill + timestamp */}
            <div className="relative z-10 flex items-start justify-between p-6">
                {isBreaking ? (
                    <div
                        data-testid={`star-breaking-pill-${item.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(0_0%_5%)] border border-[hsl(45_95%_65%/0.6)]"
                        style={{ boxShadow: "0 0 24px hsl(45 95% 65% / 0.35)" }}
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(45_95%_70%)] opacity-90 animate-ping" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(45_95%_70%)]" />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.45em] text-[hsl(45_95%_75%)] font-bold">
                            Breaking
                        </span>
                    </div>
                ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 backdrop-blur border border-[hsl(45_85%_68%/0.3)]">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(45_85%_68%)] opacity-75 animate-ping" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(45_85%_68%)]" />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-semibold">LIVE</span>
                    </div>
                )}
                <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {isBreaking ? "Pinned · Directorate" : formatRel(item.timestamp, now)}
                </span>
            </div>

            {/* center stack */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pb-2">
                <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mb-7 ring-2"
                    style={{
                        background: "linear-gradient(135deg, hsl(0 0% 8%), hsl(151 60% 7%))",
                        color: meta.accent,
                        ringColor: meta.accent,
                        boxShadow: `0 0 40px ${meta.glow}`,
                    }}
                >
                    <Icon size={36} strokeWidth={1.5} />
                </div>
                <div
                    className="text-[10px] uppercase tracking-[0.5em] mb-3"
                    style={{ color: meta.accent }}
                >
                    {item.headline}
                </div>
                <h2
                    className="font-heading text-3xl sm:text-4xl leading-[1.05] tracking-tighter mb-4 text-white"
                    style={{ textShadow: `0 4px 30px ${meta.glow}` }}
                >
                    {item.title}
                </h2>
                <p
                    className="text-base text-white/70 max-w-sm leading-relaxed"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                >
                    {item.body}
                </p>

                {/* BREAKING stat plate (Treasury Armor / Slots etc.) */}
                {isBreaking && item.stat_value && (
                    <div
                        data-testid={`star-breaking-stat-${item.id}`}
                        className="mt-6 px-6 py-3 rounded-xl bg-[hsl(0_0%_3%/0.7)] backdrop-blur border border-[hsl(45_95%_65%/0.45)]"
                    >
                        <div className="text-[9px] uppercase tracking-[0.4em] text-[hsl(45_95%_75%)] font-mono">
                            {item.stat_label}
                        </div>
                        <div
                            className="font-heading text-3xl sm:text-4xl gold-gradient-text leading-none mt-1"
                            style={{ letterSpacing: "-0.02em" }}
                        >
                            {item.stat_value}
                        </div>
                    </div>
                )}

                {item.amount && !isBreaking && (
                    <div className="mt-6 px-4 py-1.5 rounded-full bg-black/40 border border-[hsl(45_85%_68%/0.4)] font-mono text-sm text-[hsl(45_85%_75%)]">
                        ${item.amount.toLocaleString()}
                    </div>
                )}
                {item.skin_in_game > 0 && (
                    <div
                        data-testid={`star-skin-${item.id}`}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(40_60%_18%/0.5)] border border-[hsl(45_85%_68%/0.5)]"
                    >
                        <Shield size={11} className="text-[hsl(45_85%_75%)]" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_85%)] font-semibold">
                            Skin in the game · ${Number(item.skin_in_game).toLocaleString()}
                        </span>
                    </div>
                )}
                <div className="mt-4 text-[10px] uppercase tracking-[0.4em] text-white/40">
                    {item.vessel}
                    {item.state ? ` · ${item.state}` : ""}
                </div>
            </div>

            {/* action rail */}
            <div className="relative z-10 px-6 pb-7 flex items-center justify-center gap-3">
                <button
                    onClick={hype}
                    data-testid={`star-hype-${item.id}`}
                    className="group flex items-center gap-2 px-5 py-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur border border-white/10 transition-all"
                >
                    <Flame
                        size={18}
                        className={`transition-transform ${bursting ? "scale-150 text-[hsl(25_95%_65%)]" : "text-[hsl(45_85%_75%)]"}`}
                        style={{ filter: bursting ? "drop-shadow(0 0 12px hsl(25 95% 65%))" : "none" }}
                    />
                    <span className="font-mono text-sm tabular-nums text-white/85" data-testid={`star-hype-count-${item.id}`}>
                        {item.hype || 0}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 group-hover:text-white/70">
                        Hype
                    </span>
                </button>

                <button
                    onClick={() => onTransmit(item)}
                    data-testid={`star-transmit-${item.id}`}
                    className="group flex items-center gap-2 px-5 py-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur border border-white/10 transition-all"
                >
                    <Send size={16} className="text-[hsl(45_85%_75%)]" />
                    <span className="font-mono text-sm tabular-nums text-white/85" data-testid={`star-transmit-count-${item.id}`}>
                        {item.transmits || 0}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 group-hover:text-white/70">
                        Transmit
                    </span>
                </button>
            </div>
        </article>
    );
}

export default function SovereignStar() {
    const [items, setItems] = useState([]);
    const [now, setNow] = useState(new Date().toISOString());
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [hypedCache, setHypedCache] = useState(() => new Set());
    const sentinelRef = useRef(null);

    const load = useCallback(async (cur) => {
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await api.get("/sovereign-star/feed", { params: { cursor: cur, limit: 6 } });
            setNow(data.now || new Date().toISOString());
            setItems((prev) => (cur ? [...prev, ...data.items] : data.items));
            setCursor(data.next_cursor);
            setHasMore(Boolean(data.next_cursor));
        } finally {
            setLoading(false);
        }
    }, [loading]);

    useEffect(() => { load(null); /* eslint-disable-next-line */ }, []);

    useEffect(() => {
        if (!sentinelRef.current || !hasMore) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && cursor && !loading) load(cursor);
            },
            { rootMargin: "200px", threshold: 0.1 }
        );
        obs.observe(sentinelRef.current);
        return () => obs.disconnect();
    }, [cursor, hasMore, loading, load]);

    const onHype = async (item) => {
        // optimistic: cache local doubles to prevent spam, even though server is stateless on auth
        if (hypedCache.has(item.id)) {
            toast("You've already hyped this signal.");
            return;
        }
        setItems((cur) => cur.map((i) => i.id === item.id ? { ...i, hype: (i.hype || 0) + 1 } : i));
        setHypedCache((s) => new Set(s).add(item.id));
        try {
            const { data } = await api.post(`/sovereign-star/${encodeURIComponent(item.id)}/hype`);
            setItems((cur) => cur.map((i) => i.id === item.id ? { ...i, hype: data.hype, transmits: data.transmits } : i));
        } catch {
            setItems((cur) => cur.map((i) => i.id === item.id ? { ...i, hype: Math.max(0, (i.hype || 1) - 1) } : i));
        }
    };

    const onTransmit = async (item) => {
        const url = `${window.location.origin}/?signal=${encodeURIComponent(item.id)}`;
        const text = `${item.headline} · ${item.title} — Sovereign Star feed`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "EIGOO Sovereign Star", text, url });
            } else {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                toast.success("Signal copied to clipboard.");
            }
            const { data } = await api.post(`/sovereign-star/${encodeURIComponent(item.id)}/transmit`);
            setItems((cur) => cur.map((i) => i.id === item.id ? { ...i, hype: data.hype, transmits: data.transmits } : i));
        } catch (e) {
            // AbortError = user cancelled the Web Share dialog (silent UX).
            // Anything else (network, API failure) is a real bug we want to see.
            if (e?.name !== "AbortError" && e?.response) {
                console.warn("[sovereign-star] transmit failed:", e.response.status, e.response.data?.detail || e.message);
            }
        }
    };

    if (!items.length && !loading) {
        return (
            <section
                id="sovereign-star"
                className="relative max-w-3xl mx-auto px-6 py-20 text-center"
                style={{ background: "linear-gradient(180deg, hsl(0 0% 4%), hsl(151 60% 6%))" }}
                data-testid="sovereign-star"
            >
                <div className="text-muted-foreground">The Sovereign Star is warming up...</div>
            </section>
        );
    }

    return (
        <section
            id="sovereign-star"
            className="relative w-full overflow-hidden"
            style={{ background: "linear-gradient(180deg, hsl(0 0% 3%), hsl(151 60% 5%))" }}
            data-testid="sovereign-star"
        >
            {/* obsidian veil */}
            <div className="absolute inset-0 pointer-events-none opacity-30">
                <div className="absolute top-[10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[hsl(40_60%_45%/0.15)] blur-[120px]" />
                <div className="absolute bottom-[10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[hsl(153_50%_25%/0.20)] blur-[120px]" />
            </div>

            <header className="relative max-w-5xl mx-auto px-6 pt-16 pb-10 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 backdrop-blur border border-[hsl(45_85%_68%/0.3)] mb-5">
                    <Sparkles size={11} className="text-[hsl(45_85%_68%)]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-semibold">
                        Sovereign Star · Live Signal Feed
                    </span>
                </div>
                <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-white">
                    The fleet,
                    <span className="block gold-gradient-text mt-2 italic font-normal">in motion right now.</span>
                </h2>
                <p className="mt-5 text-base sm:text-lg text-white/65 max-w-xl mx-auto leading-relaxed">
                    Every boarding, every revenue delivery, every level-up — surfaced the moment it hits the bridge. Hype the loud ones. Transmit them to the world.
                </p>
                <ChevronDown className="mx-auto mt-8 text-[hsl(45_85%_68%)] animate-bounce" size={20} />
            </header>

            {/* Vertical snap-scroll runway */}
            <div
                className="relative max-w-5xl mx-auto px-6 pb-24 snap-y snap-mandatory overflow-y-auto h-[80vh] sm:h-[80vh] space-y-6"
                style={{ scrollSnapType: "y mandatory", scrollbarWidth: "thin" }}
                data-testid="star-feed-rail"
            >
                {items.map((item) => (
                    <StarCard
                        key={item.id}
                        item={item}
                        now={now}
                        onHype={onHype}
                        onTransmit={onTransmit}
                    />
                ))}

                {hasMore && (
                    <div ref={sentinelRef} className="snap-end py-8 text-center text-[10px] uppercase tracking-[0.4em] text-white/40">
                        {loading ? "Pulling next signals..." : "Scroll for more"}
                    </div>
                )}
                {!hasMore && (
                    <div className="snap-end py-8 text-center text-[10px] uppercase tracking-[0.4em] text-white/40">
                        End of the feed · check back soon
                    </div>
                )}
            </div>
        </section>
    );
}
